import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Img from 'gatsby-image';
import { getFluidGatsbyImage } from 'gatsby-storyblok-image';
import SbEditable from 'storyblok-react';

import Container from '../Container';
import ColorPicker from '../../helpers/ColorPicker';
import Shape from '../Shape';
import Button from '../Button';

const HeaderBlockBleed = ({ content }) => {
  const theme = ColorPicker(content.BgColor, content.AltColors);

  const fluidProps = getFluidGatsbyImage(content.Image.filename, {
    maxWidth: 1080,
  });

  const parseHeadline = (headline) => {
    if (typeof headline !== 'string') return null;

    if (!/(TM|™)/.test(headline)) {
      return headline;
    }

    const parts = headline.split(/(TM|™)/g);
    return parts.map((part, i) => {
      if (part === 'TM' || part === '™') {
        return (
          <span className="superscript" key={i}>
            {part}
          </span>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  return (
    <SbEditable content={content}>
      <HeaderBlockBleedWrap
        themeBackground={theme.background}
        themeDivider={theme.divider}
        themeText={theme.text}
        themeHighlight={theme.highlight}
        fullBleed={content.FullBleed}
      >
        <HeaderBlockBleedContainer flip={content.Flip} textColor={content.TextColor} overlay={content.TextOverlay} fullBleed={content.FullBleed}>
          
          <div className="overlay-text" data-overlay={content.TextOverlay}>
            {content.Subtitle && <h3 >{content.Subtitle}</h3>}
            {content.Headline && <h2>{parseHeadline(content.Headline)}</h2>}
            {content.Copy && <p>{content.Copy}</p>}
            {content.Button && content.Button.length > 0 && (
              <Button
                content={{
                  label: content.Button[0].label,
                  to: content.Button[0].to,
                  arrow: content.Button[0].arrow,
                }}
                customTheme={theme}
              />
            )}
          </div>

          <div className="image">
            <div className="image--wrap">
              <Img fluid={fluidProps} alt={content.Image.alt} />
              {content.ImageBackgroundShape && (
                <BgShape
                  shape={content.ImageBackgroundShape}
                  color={theme.highlight}
                />
              )}
            </div>
          </div>
        </HeaderBlockBleedContainer>
      </HeaderBlockBleedWrap>
    </SbEditable>
  );
};

export default HeaderBlockBleed;

const breakpoints = [640, 768, 1024, 1280, 1440];

const HeaderBlockBleedContainer = styled(Container)`
  position: relative;
  align-items: center;
  justify-content: space-between;
  direction: ${(props) => (props.flip ? 'rtl' : 'ltr')};
  margin : 0 !important;
  color: ${(props) => (props.textColor ? props.textColor : 'white')};

  > * {
    direction: ltr;
  }

  h3, h2, p {
    /*color: ${(props) => (props.textColor ? props.textColor : 'white')};*/
    color: var(--${(props) => props.textColor});
  }

  .overlay-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 20;
    text-align: center;
    background: ${(props) => (props.overlay ? 'rgba(0, 0, 0, 0.4)' : 'transparent')};
    padding: 2rem;
    border-radius: 8px;
    max-width: 80%;

    /* Add border or shadow only when overlay is false */
    ${(props) => !props.overlay && `box-shadow: 0 4px 16px rgba(0,0,0,0.15);
     border: 1px solid rgba(0,0,0,0.1);`}

    h3 {
      margin-bottom: 1.4rem;
      font-size: 3.4rem;
      line-height: 3rem;


      @media (min-width: 3000px) {
        font-size: 12.4rem;
        line-height: 17rem;
        margin-bottom: 1.4rem;
      }
   }

    h2 {
      font-family: var(--font-serif);
      font-size: 4.2rem;
      line-height: 4.8rem;
      font-weight: 700;

      @media (min-width: 1024px) {
        font-size: 5.2rem;
        line-height: 5.8rem;
      }

      @media (min-width: 3000px) {
        font-size: 15.2rem;
        line-height: 20.8rem;
        margin-bottom: 10rem;
      }
    }

    p {
      margin-top: 2.4rem;
      font-size: 2.4rem;
      line-height: 2.8rem;

      @media (min-width: 1024px) {
        font-size: 1.8rem;
        line-height: 2.4rem;
      }

      @media (min-width: 3000px) {
        font-size: 8.8rem;
        line-height: 9.4rem;
      }
    }

    .superscript {
      font-size: 0.6em;
      vertical-align: super;
    }
  }

  .content {
    width: 100%;

    @media (min-width: 768px) {
      width: 100vw;
    }

    h3 {
      margin-bottom: 1.4rem;
      color: var(--${(props) => props.themeHighlight});
      font-size: 1.4rem;
      line-height: 2rem;
    }

    h2 {
      font-family: var(--font-serif);
      font-size: 3.2rem;
      line-height: 3.8rem;
      font-weight: 700;

      @media (min-width: 1024px) {
        font-size: 4.2rem;
        line-height: 4.8rem;
      }
    }

    p {
      margin-top: 2.4rem;
      font-size: 1.4rem;
      line-height: 1.8rem;

      @media (min-width: 1024px) {
        font-size: 1.8rem;
        line-height: 2.4rem;
      }
    }

    button {
      margin-top: 6rem;
    }
  }

  .image {
    margin-top: 2.4rem;

    @media (min-width: 768px) {
      width: ${(props) => (props.fullBleed ? '100vw' : '100%')} !important;
      margin-top: unset;
    }

    .gatsby-image-wrapper {
      position: relative;
      z-index: 15;
    }

    .image--wrap {
      position: relative;

      img, .gatsby-image-wrapper img {
        object-fit: ${(props) => (props.fullBleed ? 'cover' : 'contain')};
        object-position: center;
        width: ${(props) => (props.fullBleed ? '100vw' : '100%')} !important;
        height: ${(props) => (props.fullBleed ? '100%' : 'auto')} !important;
      }
    }
  }

  ${breakpoints.map(
    (bp) => `@media(min-width: ${bp}px) { max-width: 100vw !important; }`
  )}
`;

const HeaderBlockBleedWrap = styled.section`
  margin-top: -65px !important;
  padding: 0 !important;
  background-color: var(--${(props) => props.themeBackground});
  color: var(--${(props) => props.themeText});

  width: ${(props) => (props.fullBleed ? '100vw' : 'auto')};
  margin-left: ${(props) => (props.fullBleed ? '50%' : 'auto')};
  transform: ${(props) => (props.fullBleed ? 'translateX(-50%)' : 'none')};

  @media (min-width: 640px) {
    padding: 4.2rem;
  }
`;

const BgShape = styled(Shape)`
  display: flex;
  position: absolute;
  z-index: 10;
  top: 0;
  left: 0;
  align-content: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  opacity: 0.1;
`;

HeaderBlockBleed.propTypes = {
  content: PropTypes.shape({
    TextColor: PropTypes.string,
    BgColor: PropTypes.string,
    AltColors: PropTypes.bool,
    Subtitle: PropTypes.string,
    Headline: PropTypes.string,
    Copy: PropTypes.string,
    Button: PropTypes.array,
    Image: PropTypes.object,
    ImageBackgroundShape: PropTypes.string,
    Flip: PropTypes.bool,
    FullBleed: PropTypes.bool,
    TextOverlay: PropTypes.bool,
  }),
};

HeaderBlockBleed.defaultProps = {
  content: {
    TextColor: 'white',
    BgColor: 'white',
    AltColors: false,
    Subtitle: null,
    Headline: null,
    Copy: null,
    Button: null,
    Image: null,
    ImageBackgroundShape: null,
    Flip: false,
    FullBleed: false,
    TextOverlay: false,
  },
};
